import React from 'react';
import PropTypes from 'prop-types';
import Button from '@strava/ui/Button';
import I18n from 'utils/I18n';
import styles from '../styles.scss';
import useFeedAnalytics from '../../../../hooks/useFeedAnalytics';

import { mapProps } from '../../../../props/feed-prop-types';
import { useFeedEntityContext } from '../../../../state/FeedEntityContext';

const tag = (label, className) => (
  <div
    data-testid="tag"
    key={label}
    className={`${styles.activityMapTag} ${className || ''}`}
  >
    {I18n.t(label)}
  </div>
);

const buildActivityMapTags = ({
  isVirtual,
  isCommute,
  workoutType,
  privacyTagKey
}) => {
  const tags = [];
  if (privacyTagKey) {
    tags.push(tag(`templates.feed.activity.tag.${privacyTagKey}`));
  }
  if (isVirtual) {
    tags.push(tag('templates.feed.activity.tag.virtual'));
  }
  if (isCommute) {
    tags.push(tag('templates.feed.activity.tag.commute'));
  }
  if (workoutType) {
    const className = workoutType === 'race' ? styles.tagRace : '';
    tags.push(tag(`templates.feed.activity.tag.${workoutType}`, className));
  }
  return tags;
};

const Map = ({
  activityMap,
  isRoutable,
  activityId,
  entryTypeClassName,
  isVirtual,
  isCommute,
  isGroupActivity,
  workoutType,
  privacyTagKey
}) => {
  const { entityState, analyticProps } = useFeedEntityContext() || {};
  const elements = [];

  const { intersectionObserverRef } = useFeedAnalytics({
    ...analyticProps,
    element: 'map'
  });

  // "Try this route" button on maps
  const handleRouteButtonClick = () => {
    entityState.trackAnalytics({
      action: 'click',
      element: 'try_route'
    });
    window.location.href = `/activities/${activityId}/route`;
  };

  // "Try this Route" button
  if (isRoutable) {
    elements.push(
      <div
        data-testid="try-route"
        key="try-route"
        className={styles.activityMapCreateRoute}
      >
        <Button
          type="button"
          variant="default"
          className={`${styles.btn} ${styles.btnSm} ${styles.textBold}`}
          onClick={() => handleRouteButtonClick(activityId)}
        >
          {I18n.t('templates.feed.activity.try_route')}
        </Button>
      </div>
    );
  }

  const alt =
    activityMap.alt ||
    (isGroupActivity
      ? I18n.t('templates.feed.activity.group_map_alt')
      : I18n.t('templates.feed.activity.map_alt'));

  // Map image
  elements.push(
    <a
      onClick={() => {
        entityState.trackAnalytics({
          action: 'click',
          element: 'map'
        });
      }}
      key="map"
      href={`/activities/${activityId}`}
    >
      <div className={styles.activityMapTags}>
        {buildActivityMapTags({
          isVirtual,
          isCommute,
          workoutType,
          privacyTagKey
        })}
      </div>
      <img
        data-testid="map"
        src={activityMap.url}
        srcSet={activityMap.url}
        alt={alt}
        className={entryTypeClassName}
      />
    </a>
  );
  return (
    <div
      ref={intersectionObserverRef}
      className={`${entryTypeClassName} ${styles.entryImage}`}
    >
      <div className={styles.entryImageWrapper}>{elements}</div>
    </div>
  );
};

Map.propTypes = {
  activityId: PropTypes.string.isRequired,
  isRoutable: PropTypes.bool.isRequired,
  activityMap: mapProps.isRequired,
  entryTypeClassName: PropTypes.oneOf([
    styles.activityMap,
    styles.groupActivityMap
  ]).isRequired,
  isVirtual: PropTypes.bool,
  isCommute: PropTypes.bool,
  isGroupActivity: PropTypes.bool,
  workoutType: PropTypes.oneOf(['race', 'long_run', 'workout']),
  privacyTagKey: PropTypes.string
};

Map.defaultProps = {
  workoutType: null,
  isVirtual: false,
  isCommute: false,
  isGroupActivity: false,
  privacyTagKey: null
};

export default Map;
